import { useEffect, useRef, useState } from 'react'
import { apiPost, apiGet } from '../../api'
import MercedesIcon from './MercedesIcon'

interface SendChannels {
  gmail: boolean
  microsoft: Array<{ label: string; email: string | null }>
}

// Web Speech API typings — minimal shim
interface SpeechRecognitionInstance extends EventTarget {
  start: () => void
  stop: () => void
  continuous: boolean
  interimResults: boolean
  lang: string
  onresult: ((e: { results: { transcript: string }[][] }) => void) | null
  onend: (() => void) | null
  onerror: ((e: { error: string }) => void) | null
}
declare global {
  interface Window {
    SpeechRecognition?: { new(): SpeechRecognitionInstance }
    webkitSpeechRecognition?: { new(): SpeechRecognitionInstance }
  }
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  drafts?: EmailDraft[]
  extracted?: ExtractedTask[]
}
interface EmailDraft { to: string; subject: string; body: string }
interface ExtractedTask { id: string; title: string; repo_label: string; importance: string | null; due_at: number | null }
interface ChatResponse {
  reply: string
  drafts?: EmailDraft[]
  tools?: Array<{ action: string }>
  model?: string
}
interface ExtractResponse { extracted: number; emails_processed: number; fresh: ExtractedTask[] }

const HISTORY_KEY = 'jamie-dashboard:mercedes-history'
const POSITION_KEY = 'jamie-dashboard:mercedes-fab-position'

function loadHistory(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    return raw ? JSON.parse(raw) as ChatMessage[] : []
  } catch { return [] }
}
function saveHistory(h: ChatMessage[]) {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(h.slice(-40))) } catch { /* ignore */ }
}

export default function Mercedes() {
  const [open, setOpen] = useState(false)
  const [history, setHistory] = useState<ChatMessage[]>(() => loadHistory())
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  // FAB position — drag to move
  const [fabPos, setFabPos] = useState<{ x: number; y: number }>(() => {
    try {
      const raw = localStorage.getItem(POSITION_KEY)
      return raw ? JSON.parse(raw) as { x: number; y: number } : { x: 24, y: 24 }
    } catch { return { x: 24, y: 24 } }
  })
  const dragRef = useRef<{ sx: number; sy: number; px: number; py: number; moved: boolean } | null>(null)

  const [channels, setChannels] = useState<SendChannels | null>(null)
  useEffect(() => {
    if (!open) return
    apiGet<SendChannels>('/mercedes/accounts').then(setChannels).catch(() => undefined)
  }, [open])

  // Voice input
  const [recording, setRecording] = useState(false)
  const recognizerRef = useRef<SpeechRecognitionInstance | null>(null)
  function toggleVoice() {
    if (recording) {
      recognizerRef.current?.stop()
      return
    }
    const Recog = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!Recog) { alert('Speech recognition not supported in this browser.'); return }
    const r = new Recog()
    r.continuous = false
    r.interimResults = false
    r.lang = 'en-GB'
    r.onresult = (e) => {
      const transcript = e.results?.[0]?.[0]?.transcript ?? ''
      if (transcript) setInput(prev => prev ? prev + ' ' + transcript : transcript)
    }
    r.onend = () => setRecording(false)
    r.onerror = () => setRecording(false)
    recognizerRef.current = r
    r.start()
    setRecording(true)
  }

  useEffect(() => { saveHistory(history) }, [history])
  // Listen for cross-component "open Mercedes with prompt" events
  useEffect(() => {
    function onPrompt(e: Event) {
      const detail = (e as CustomEvent).detail as { prompt?: string } | undefined
      if (!detail?.prompt) return
      setOpen(true)
      setInput(detail.prompt)
    }
    window.addEventListener('mercedes:open', onPrompt as EventListener)
    return () => window.removeEventListener('mercedes:open', onPrompt as EventListener)
  }, [])
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [history, open])

  function onFabPointerDown(e: React.PointerEvent) {
    dragRef.current = { sx: e.clientX, sy: e.clientY, px: fabPos.x, py: fabPos.y, moved: false }
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
  }
  function onFabPointerMove(e: React.PointerEvent) {
    if (!dragRef.current) return
    const d = dragRef.current
    const dx = e.clientX - d.sx
    const dy = e.clientY - d.sy
    if (!d.moved && Math.hypot(dx, dy) < 4) return
    d.moved = true
    const next = {
      x: Math.max(8, d.px - dx),
      y: Math.max(8, d.py - dy),
    }
    setFabPos(next)
  }
  function onFabPointerUp(e: React.PointerEvent) {
    const d = dragRef.current
    dragRef.current = null
    ;(e.target as Element).releasePointerCapture?.(e.pointerId)
    if (d) {
      try { localStorage.setItem(POSITION_KEY, JSON.stringify(fabPos)) } catch { /* ignore */ }
      if (!d.moved) setOpen(v => !v)
    }
  }

  async function send() {
    const text = input.trim()
    if (!text || busy) return
    const next: ChatMessage[] = [...history, { role: 'user', content: text }]
    setHistory(next); setInput(''); setBusy(true); setErr(null)
    try {
      const res = await apiPost<ChatResponse>('/mercedes/chat', {
        messages: next.map(m => ({ role: m.role, content: m.content })),
      })
      // If Mercedes asked for an extract, fire it now and include the result
      let extracted: ExtractedTask[] | undefined
      if (res.tools?.some(t => t.action === 'extract_inbox_tasks')) {
        try {
          const er = await apiPost<ExtractResponse>('/mercedes/extract-tasks')
          extracted = er.fresh
        } catch (e) { /* fall through */ }
      }
      setHistory(h => [...h, { role: 'assistant', content: res.reply, drafts: res.drafts, extracted }])
    } catch (e) {
      setErr(String(e))
    } finally {
      setBusy(false)
    }
  }
  function clearChat() {
    setHistory([])
    setErr(null)
  }
  function copyDraft(d: EmailDraft) {
    const text = `To: ${d.to}\nSubject: ${d.subject}\n\n${d.body}`
    navigator.clipboard?.writeText(text).catch(() => undefined)
  }

  const [sendingDraft, setSendingDraft] = useState<string | null>(null)
  const [sendStatus, setSendStatus] = useState<{ key: string; ok: boolean; msg: string } | null>(null)
  async function sendDraft(d: EmailDraft, account: string, key: string) {
    if (sendingDraft) return
    if (!d.to || !d.subject) {
      setSendStatus({ key, ok: false, msg: 'to + subject required' })
      return
    }
    if (!confirm(`Send via ${account}?\nTo: ${d.to}\nSubject: ${d.subject}`)) return
    setSendingDraft(key)
    setSendStatus(null)
    try {
      const res = await apiPost<{ ok: boolean; msg?: string }>('/mercedes/send', {
        account, to: d.to, subject: d.subject, body: d.body,
      })
      setSendStatus({ key, ok: !!res.ok, msg: res.ok ? `sent via ${account}` : (res.msg ?? 'failed') })
    } catch (e) {
      setSendStatus({ key, ok: false, msg: String(e) })
    } finally {
      setSendingDraft(null)
    }
  }

  return (
    <>
      {/* Floating action button */}
      <button
        type="button"
        className="mercedes-fab"
        aria-label="Mercedes assistant"
        style={{ right: fabPos.x, bottom: fabPos.y }}
        onPointerDown={onFabPointerDown}
        onPointerMove={onFabPointerMove}
        onPointerUp={onFabPointerUp}
        onPointerCancel={() => { dragRef.current = null }}>
        <MercedesIcon size={26} color="currentColor" />
      </button>

      {/* Slide-up chat */}
      {open && (
        <div className="mercedes-panel" role="dialog" aria-label="Mercedes chat">
          <header className="mercedes-header">
            <span className="mercedes-title">
              <MercedesIcon size={18} />
              <span>Mercedes</span>
            </span>
            <span style={{ display: 'flex', gap: 4 }}>
              {history.length > 0 && (
                <button type="button" onClick={clearChat}
                  style={{ minHeight: 24, padding: '2px 8px', fontSize: '0.65rem' }}>
                  clear
                </button>
              )}
              <button type="button" onClick={() => setOpen(false)}
                style={{ minHeight: 24, minWidth: 24, padding: '0 6px', fontSize: '0.85rem' }}>×</button>
            </span>
          </header>
          <div ref={scrollRef} className="mercedes-messages">
            {history.length === 0 && (
              <div className="mercedes-welcome">
                <p style={{ margin: 0 }}>Hi Jamie. Ask me to draft an email, summarise a thread, or just chat.</p>
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Suggestion onClick={() => setInput('Extract action items from my inbox')}>Extract tasks from inbox</Suggestion>
                  <Suggestion onClick={() => setInput('Draft a polite chase email to Paul about the SOC2 review')}>Draft chase to Paul</Suggestion>
                  <Suggestion onClick={() => setInput('Summarise my inbox in one paragraph')}>Summarise inbox</Suggestion>
                  <Suggestion onClick={() => setInput("Draft a one-line out-of-office for tomorrow")}>Draft an OOO</Suggestion>
                </div>
              </div>
            )}
            {history.map((m, i) => (
              <div key={i} className={`mercedes-msg ${m.role}`}>
                {m.content && <div className="mercedes-msg-text">{m.content}</div>}
                {m.extracted && (
                  <div className="mercedes-draft" style={{
                    background: 'rgba(125,211,252,0.05)',
                    borderColor: 'rgba(125,211,252,0.30)',
                  }}>
                    <div className="mercedes-draft-head">
                      <span className="mono" style={{ color: 'var(--accent-2)' }}>
                        {m.extracted.length} ACTION{m.extracted.length === 1 ? '' : 'S'} FROM INBOX
                      </span>
                    </div>
                    {m.extracted.length === 0 ? (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        Nothing actionable in your recent inbox.
                      </div>
                    ) : (
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {m.extracted.map(t => (
                          <li key={t.id} style={{
                            display: 'flex', alignItems: 'baseline', gap: 6,
                            fontSize: '0.78rem', padding: '3px 0',
                          }}>
                            <span style={{
                              flex: '0 0 auto', padding: '1px 6px', borderRadius: 999,
                              fontFamily: 'var(--font-mono)', fontSize: '0.6rem',
                              letterSpacing: '0.08em',
                              background: t.importance === 'high' ? 'rgba(255,107,129,0.16)' : 'rgba(154,169,191,0.10)',
                              color: t.importance === 'high' ? 'var(--critical)' : 'var(--text-muted)',
                              border: t.importance === 'high' ? '1px solid rgba(255,107,129,0.4)' : '1px solid rgba(154,169,191,0.2)',
                            }}>
                              {(t.importance || 'normal').toUpperCase()}
                            </span>
                            <span style={{ flex: 1, minWidth: 0 }}>{t.title}</span>
                            {t.due_at && (
                              <span className="muted mono" style={{ fontSize: '0.65rem' }}>
                                {new Date(t.due_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div style={{ marginTop: 6, fontSize: '0.66rem', color: 'var(--text-muted)' }}>
                      Added to your Open Tasks panel.
                    </div>
                  </div>
                )}
                {m.drafts && m.drafts.map((d, j) => {
                  const draftKey = `${i}:${j}`
                  const status = sendStatus?.key === draftKey ? sendStatus : null
                  const sending = sendingDraft === draftKey
                  return (
                    <div key={j} className="mercedes-draft">
                      <div className="mercedes-draft-head">
                        <span className="mono">DRAFT</span>
                        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button type="button" onClick={() => copyDraft(d)}
                            style={{ minHeight: 22, padding: '2px 8px', fontSize: '0.65rem' }}>
                            copy
                          </button>
                          {channels?.gmail && (
                            <button type="button"
                              disabled={sending}
                              onClick={() => sendDraft(d, 'gmail', draftKey)}
                              style={{ minHeight: 22, padding: '2px 8px', fontSize: '0.65rem',
                                       borderColor: 'rgba(61,220,132,0.4)', color: 'var(--ok)' }}>
                              ↗ gmail
                            </button>
                          )}
                          {channels?.microsoft.map(ms => (
                            <button key={ms.label} type="button"
                              disabled={sending}
                              onClick={() => sendDraft(d, `ms:${ms.label}`, draftKey)}
                              title={ms.email ?? undefined}
                              style={{ minHeight: 22, padding: '2px 8px', fontSize: '0.65rem',
                                       borderColor: 'rgba(90,242,255,0.4)', color: 'var(--accent)' }}>
                              ↗ {ms.label}
                            </button>
                          ))}
                        </span>
                      </div>
                      <div className="mercedes-draft-meta">
                        <div><span className="mono muted">To:</span> {d.to || '—'}</div>
                        <div><span className="mono muted">Subject:</span> {d.subject}</div>
                      </div>
                      <pre className="mercedes-draft-body">{d.body}</pre>
                      {status && (
                        <div style={{ marginTop: 6, fontSize: '0.7rem',
                                      color: status.ok ? 'var(--ok)' : 'var(--critical)' }}>
                          {status.ok ? '✓' : '⚠'} {status.msg}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ))}
            {busy && <div className="mercedes-msg assistant"><div className="mercedes-msg-text muted">…thinking</div></div>}
            {err && <div className="mercedes-msg assistant"><div className="mercedes-msg-text" style={{ color: 'var(--critical)' }}>{err}</div></div>}
          </div>
          <form className="mercedes-input"
                onSubmit={e => { e.preventDefault(); send() }}>
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder='ask me to draft, summarise, or anything…'
              disabled={busy}
              autoFocus
            />
            <button type="button"
              onClick={toggleVoice}
              disabled={busy}
              aria-label={recording ? 'Stop recording' : 'Voice input'}
              style={{ background: recording ? 'rgba(255,107,129,0.18)' : 'rgba(255,255,255,0.04)',
                       borderColor: recording ? 'rgba(255,107,129,0.5)' : 'var(--border-soft)',
                       color: recording ? 'var(--critical)' : 'var(--text-secondary)' }}>
              {recording ? '●' : '🎤'}
            </button>
            <button type="submit" disabled={busy || input.trim().length === 0}>
              ↑
            </button>
          </form>
        </div>
      )}
    </>
  )
}

function Suggestion({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="mercedes-suggestion">
      {children}
    </button>
  )
}
